/**
 * A byte source that keeps recently read blocks.
 *
 * MP3 and WebM files have no index of where their sound is, so a stretch is
 * read twice: once to find the frames (walking frame or block headers), once
 * to decode them. Keeping the last few megabytes means the second read costs
 * nothing, for a remote file as much as a local one.
 */

import type { ByteSource } from './byteSource';

export interface CacheOptions {
  /** Bytes per cached block (reads are rounded out to these). */
  blockBytes?: number;
  /** Blocks kept; the least recently used goes first. */
  maxBlocks?: number;
}

export const cachedByteSource = (source: ByteSource, options: CacheOptions = {}): ByteSource => {
  const blockBytes = Math.max(4096, options.blockBytes ?? 1024 * 1024);
  const maxBlocks = Math.max(2, options.maxBlocks ?? 48);
  const blocks = new Map<number, Uint8Array>();

  const remember = (index: number, bytes: Uint8Array) => {
    blocks.delete(index);
    blocks.set(index, bytes);
    while (blocks.size > maxBlocks) {
      const oldest = blocks.keys().next();
      if (oldest.done) break;
      blocks.delete(oldest.value);
    }
  };

  return {
    size: source.size,
    async read(offset, length, signal) {
      const start = Math.max(0, Math.min(source.size, offset));
      const end = Math.max(start, Math.min(source.size, offset + length));
      if (end === start) return new Uint8Array(0);
      const first = Math.floor(start / blockBytes);
      const last = Math.floor((end - 1) / blockBytes);
      // This read's blocks are kept here too: a read bigger than the cache
      // would otherwise push out its own first blocks.
      const parts: Uint8Array[] = new Array(last - first + 1);
      for (let index = first; index <= last;) {
        const cached = blocks.get(index);
        if (cached) {
          parts[index - first] = cached;
          remember(index, cached);
          index += 1;
          continue;
        }
        // Fetch each run of missing blocks with one request.
        let runEnd = index;
        while (runEnd + 1 <= last && !blocks.has(runEnd + 1)) runEnd += 1;
        const from = index * blockBytes;
        const to = Math.min(source.size, (runEnd + 1) * blockBytes);
        // eslint-disable-next-line no-await-in-loop
        const bytes = await source.read(from, to - from, signal);
        for (let block = index; block <= runEnd; block += 1) {
          const at = (block - index) * blockBytes;
          const part = bytes.subarray(Math.min(bytes.length, at), Math.min(bytes.length, at + blockBytes));
          parts[block - first] = part;
          remember(block, part);
        }
        index = runEnd + 1;
      }
      const out = new Uint8Array(end - start);
      for (let index = first; index <= last; index += 1) {
        const bytes = parts[index - first];
        const blockStart = index * blockBytes;
        const from = Math.max(start, blockStart);
        const to = Math.min(end, blockStart + bytes.length);
        if (to > from) out.set(bytes.subarray(from - blockStart, to - blockStart), from - start);
      }
      return out;
    },
  };
};
